import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/CartContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import TyresList from "./pages/TyresList";
import TyreDetail from "./pages/TyreDetail";
import MobileFitting from "./pages/MobileFitting";
import EmergencyTools from "./pages/EmergencyTools";
import EmergencyToolDetail from "./pages/EmergencyToolDetail";
import Checkout from "./pages/Checkout";
import Confirmation from "./pages/Confirmation";
import Contact from "./pages/Contact";
import OrderTrack from "./pages/OrderTrack";
import RecoveryRequest from "./pages/RecoveryRequest";
import PartsList from "./pages/PartsList";
import PartDetail from "./pages/PartDetail";
import WhatsAppButton from "./components/WhatsAppButton";
import { Toaster } from "sonner";

// Admin
import { AdminAuthProvider } from "./admin/auth";
import AdminProtected from "./admin/AdminProtected";
import AdminLayout from "./admin/AdminLayout";
import AdminLogin from "./admin/pages/AdminLogin";
import AdminDashboard from "./admin/pages/AdminDashboard";
import AdminProducts from "./admin/pages/AdminProducts";
import AdminProductForm from "./admin/pages/AdminProductForm";
import AdminProductImport from "./admin/pages/AdminProductImport";
import AdminStock from "./admin/pages/AdminStock";
import AdminStockDetail from "./admin/pages/AdminStockDetail";
import AdminOrders from "./admin/pages/AdminOrders";
import AdminOrderDetail from "./admin/pages/AdminOrderDetail";
import AdminRevenue from "./admin/pages/AdminRevenue";
import AdminAnalytics from "./admin/pages/AdminAnalytics";
import AdminReviews from "./admin/pages/AdminReviews";
import AdminSettings from "./admin/pages/AdminSettings";
import AdminParts from "./admin/pages/AdminParts";
import AdminPartForm from "./admin/pages/AdminPartForm";
import AdminPartsImport from "./admin/pages/AdminPartsImport";
import AdminRecovery from "./admin/pages/AdminRecovery";

// Public chrome (Header/Footer/WhatsApp) is hidden on any /admin/* route so the
// admin CMS sits in its own sidebar layout without the marketing chrome bleeding in.
const PublicChrome = ({ children }) => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return children;
  return (
    <>
      <Header />
      <main className="vd-page">{children}</main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <CartProvider>
          <AdminAuthProvider>
            <div className="App">
              <BrowserRouter>
                <PublicChrome>
                  <Routes>
                    {/* Public site */}
                    <Route path="/" element={<Home />} />
                    <Route path="/tyres" element={<TyresList />} />
                    <Route path="/tyres/:id" element={<TyreDetail />} />
                    <Route path="/mobile-fitting" element={<MobileFitting />} />
                    <Route path="/emergency-tools" element={<EmergencyTools />} />
                    <Route path="/emergency-tools/:id" element={<EmergencyToolDetail />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/confirmation/:id" element={<Confirmation />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/order/track" element={<OrderTrack />} />
                    <Route path="/recovery" element={<RecoveryRequest />} />
                    <Route path="/parts" element={<PartsList />} />
                    <Route path="/parts/:id" element={<PartDetail />} />

                    {/* Admin */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminProtected><AdminLayout /></AdminProtected>}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="products" element={<AdminProducts />} />
                      <Route path="products/import" element={<AdminProductImport />} />
                      <Route path="products/new" element={<AdminProductForm />} />
                      <Route path="products/:id" element={<AdminProductForm />} />
                      <Route path="stock" element={<AdminStock />} />
                      <Route path="stock/:id" element={<AdminStockDetail />} />
                      <Route path="orders" element={<AdminOrders />} />
                      <Route path="orders/:id" element={<AdminOrderDetail />} />
                      <Route path="revenue" element={<AdminRevenue />} />
                      <Route path="analytics" element={<AdminAnalytics />} />
                      <Route path="reviews" element={<AdminReviews />} />
                      <Route path="parts" element={<AdminParts />} />
                      <Route path="parts/import" element={<AdminPartsImport />} />
                      <Route path="parts/new" element={<AdminPartForm />} />
                      <Route path="parts/:id" element={<AdminPartForm />} />
                      <Route path="recovery" element={<AdminRecovery />} />
                      <Route path="settings" element={<AdminSettings />} />
                    </Route>

                    <Route path="*" element={<Home />} />
                  </Routes>
                </PublicChrome>
              </BrowserRouter>
              <Toaster richColors position="top-right" />
            </div>
          </AdminAuthProvider>
        </CartProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
